import { useEffect, memo, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import clsx from 'clsx';
// mantine
import { useDebouncedValue } from '@mantine/hooks';
// components
import { TPersona } from '@newo/api/gateway';
import CallLogItem from '@/modules/Analytics/components/SystemOutput/components/CallLogItem';
import OperationLogItem from '@/modules/Analytics/components/SystemOutput/components/OperationLogItem';
import SystemLogItem from '@/modules/Analytics/components/SystemOutput/components/SystemLogItem';
// utils
import useViewport from '@/utils/hooks/useViewport.ts';
import useFiltersToParams from '@/modules/Analytics/Analytics.hooks.ts';
import { usePaginatedLogsQuery } from '@/services/analytics/analytics.queries.ts';
import useAnalyticsStore from '@/modules/Analytics/Analytics.store.ts';
// assets
import styles from '@/modules/Analytics/components/SystemOutput/SystemOutput.module.css';

interface SystemOutputProps {
    agents: Record<string, TPersona>;
    className?: string;
    searchTerm?: string;
    isAgentsFetching: boolean;
}

const DEBOUNCE_TIMEOUT = 300;
const START_PAGE = 1;
const EMPTY_LOGS_LENGTH = 0;
const SCROLL_Y_TOP = 0;

const SystemOutput = ({
    agents,
    className,
    searchTerm,
    isAgentsFetching
}: SystemOutputProps) => {
    const { ref, viewport, isIntersecting, scrollToBottom } = useViewport({
        rootMargin: '1000px 0px 0px 0px'
    });
    const { isAnalyticsOpened, setLoading } = useAnalyticsStore(
        useShallow((state) => ({
            isAnalyticsOpened: state.isAnalyticsOpened,
            setFilters: state.setFilters,
            setLoading: state.setLoading
        }))
    );

    const queryClient = useQueryClient();
    const { params } = useFiltersToParams();
    const [debounced] = useDebouncedValue(params, DEBOUNCE_TIMEOUT);
    const {
        data,
        hasNextPage,
        fetchNextPage,
        isFetching: isLogsFetching
    } = usePaginatedLogsQuery(debounced, isAnalyticsOpened);
    const page = data?.pages.length ?? 1;

    useEffect(() => {
        setLoading(isAgentsFetching || isLogsFetching);
    }, [isLogsFetching, isAgentsFetching, setLoading]);

    const logs = useMemo(
        () => (data?.pages ?? []).flatMap((p) => p.items),
        [data]
    );

    useEffect(() => {
        if (isIntersecting && hasNextPage) {
            fetchNextPage();
        }
    }, [isIntersecting, hasNextPage, fetchNextPage]);

    useEffect(() => {
        if (page === START_PAGE) {
            scrollToBottom();
        }
    }, [page, logs, scrollToBottom, viewport]);

    useEffect(() => {
        if (
            logs.length > EMPTY_LOGS_LENGTH &&
            viewport.current?.scrollTop === SCROLL_Y_TOP
        ) {
            viewport.current.scrollTo({
                top: 100
            });
        }
    }, [logs, scrollToBottom, viewport]);

    // remove history of paginated logs
    useEffect(() => {
        return () => {
            queryClient.removeQueries({
                queryKey: ['analytics/logs/paginated']
            });
        };
    }, [queryClient]);

    return (
        <div className={clsx(styles.output, className)} ref={viewport}>
            {logs.map((log) => {
                switch (log.log_type) {
                    case 'system': {
                        return (
                            <SystemLogItem
                                datetime={log.datetime}
                                key={log.log_id}
                                level={log.level}
                                message={log.message}
                                searchTerm={searchTerm}
                            />
                        );
                    }
                    case 'operation': {
                        return (
                            <OperationLogItem
                                datetime={log.datetime}
                                key={log.log_id}
                                level={log.level}
                                log={log}
                                message={log.message}
                                searchTerm={searchTerm}
                            />
                        );
                    }
                    case 'call': {
                        return (
                            <CallLogItem
                                agent={
                                    agents[log.data.context.agent_persona_id]
                                }
                                datetime={log.datetime}
                                key={log.log_id}
                                level={log.level}
                                log={log}
                                message={log.message}
                                searchTerm={searchTerm}
                            />
                        );
                    }
                    default: {
                        return;
                    }
                }
            })}
            <div ref={ref} />
        </div>
    );
};

export default memo(SystemOutput);
